import { Alert, App as AntApp, Form, Input, InputNumber, Modal, Select } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { balanceKeys, formatSum, toAmount, type FamilyBalance } from "../app/balance.ts";

const noteMaxLength = 300;

/** Field names match the API (fromStudentId, toStudentId, amount, note) so server field errors land under the right input. */
interface TransferForm {
  fromStudentId?: string;
  toStudentId?: string;
  amount?: number;
  note?: string;
}

interface Props {
  family: FamilyBalance;
  open: boolean;
  onClose(): void;
}

/**
 * Moves credit from one child to a sibling (balance.manage). The API records two ledger entries and refuses more than the
 * giver's credit; the form checks the same limit first.
 */
export function CreditTransferModal({ family, open, onClose }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<TransferForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const fromStudentId = Form.useWatch("fromStudentId", form);
  const givers = family.students.filter((s) => toAmount(s.balance) > 0);
  const available = toAmount(family.students.find((s) => s.studentId === fromStudentId)?.balance ?? 0);
  const name = (s: { firstName: string; lastName: string }) => `${s.lastName} ${s.firstName}`;

  useEffect(() => {
    if (open) {
      form.resetFields();
      const onlyGiver = givers.length === 1 ? givers[0] : undefined;
      if (onlyGiver) form.setFieldsValue({ fromStudentId: onlyGiver.studentId });
      setSummary([]);
    }
    // Reset only when the dialog opens, not when the family's balances refresh after a transfer.
  }, [open, form]);

  const mutation = useMutation({
    mutationFn: async (values: TransferForm) => {
      const { data, error, response } = await api.POST("/balance/families/{familyId}/transfers", {
        params: { path: { familyId: family.familyId } },
        body: {
          fromStudentId: values.fromStudentId ?? null,
          toStudentId: values.toStudentId ?? null,
          amount: values.amount ?? null,
          note: values.note?.trim() || null,
        },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (transfer) => {
      queryClient.setQueryData(balanceKeys.family(family.familyId), transfer.family);
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
      void message.success(t("balance.transfer.done"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const required = [{ required: true, message: t("family.validation.required") }];
  return (
    <Modal
      open={open}
      title={t("balance.transfer.title")}
      okText={t("balance.transfer.submit")}
      cancelText={t("family.cancel")}
      confirmLoading={mutation.isPending}
      okButtonProps={{ disabled: givers.length === 0 }}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("balance.transfer.hint")} />
      <Form<TransferForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => mutation.mutate(values)}>
        <Form.Item
          name="fromStudentId"
          label={t("balance.transfer.from")}
          rules={required}
          extra={fromStudentId ? t("balance.transfer.available", { amount: formatSum(available) }) : undefined}
        >
          <Select
            options={givers.map((s) => ({ value: s.studentId, label: name(s) }))}
            onChange={() => void form.validateFields(["amount"]).catch(() => undefined)}
            notFoundContent={t("balance.transfer.noCredit")}
          />
        </Form.Item>
        <Form.Item name="toStudentId" label={t("balance.transfer.to")} rules={required} dependencies={["fromStudentId"]}>
          <Select options={family.students.filter((s) => s.studentId !== fromStudentId).map((s) => ({ value: s.studentId, label: name(s) }))} />
        </Form.Item>
        <Form.Item
          name="amount"
          label={t("balance.transfer.amount")}
          dependencies={["fromStudentId"]}
          rules={[
            ...required,
            {
              validator: async (_, value: number | undefined) => {
                if (value === undefined || value === null) return;
                if (value <= 0) throw new Error(t("balance.transfer.positive"));
                if (fromStudentId && value > available) throw new Error(t("balance.transfer.tooMuch"));
              },
            },
          ]}
        >
          <InputNumber<number> style={{ width: "100%" }} min={0} precision={2} step={10000} />
        </Form.Item>
        <Form.Item name="note" label={t("balance.transfer.note")}>
          <Input.TextArea rows={2} maxLength={noteMaxLength} showCount placeholder={t("balance.transfer.notePlaceholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
